import React, { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { Checkbox } from 'ui-components/components/checkbox';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex';

import { INSURANCE_WALLET_TOGGLE } from '../../../constants/analytics-events';
import { useTrackInsuranceEvent } from '../../../insurance/hooks/use-track-insurance-event';
import { EventSourceStrings } from '../../../tracking/tracking-constants';
import { TermLink } from '../../components/term-link';
import { AcceptTerms } from '../../components/accept-terms';
import { useWalletRegistrationModalContext } from '../wallet-registration/contexts/wallet-registration-modal-context';
import { useWalletRegistrationFormContext } from '../wallet-registration/contexts/wallet-registration-form-context';
import { messages as termsMessages } from '../wallet-registration/steps/terms-step/messages';
import { FORM_REQUIREMENTS } from '../../utilities/constants';
import { Versions } from '../../../services/link-service';
import { DEFAULT_WALLET_TERMS_VERSION } from '../../../pages/settings/carriers/constants';
import { emptyArray } from 'ui-components/default-props/empty';
import { getInsuranceProviderById } from '../../../enums/insurance-providers';
import { InsuranceTermsLink } from '../../../insurance/terms-link';

import { messages } from '../../messages';
import styles from './carrier-update-modal.module.css';

export const TermsAndConditions = ({ setCanProgress, newInsuranceProviderIds = emptyArray }) => {
  const intl = useIntl();
  const { terms, shouldAutoInsure, setShouldAutoInsure, shouldShowInsuranceToggle, nonDefaultTerms } = useWalletRegistrationModalContext();
  const { walletForm, updateWalletForm } = useWalletRegistrationFormContext();
  const hasInsurance = newInsuranceProviderIds.length > 0;
  const insuranceProvider = hasInsurance ? getInsuranceProviderById(newInsuranceProviderIds[0]) : null;
  const insuranceProviderName = insuranceProvider && intl.formatMessage(insuranceProvider.messageDescriptor);
  const trackWalletToggle = useTrackInsuranceEvent(INSURANCE_WALLET_TOGGLE, EventSourceStrings.ADD_FUNDS_WALLET_REGISTRATION_STEP);

  const hasNormalTerms = terms?.length > 0;
  const hasNonDefaultTerms = Object.keys(nonDefaultTerms).length > 0;

  useEffect(() => {
    const hasOnlyNonDefaultTerms = hasNonDefaultTerms && !hasNormalTerms;
    const hasNoTerms = !hasNormalTerms && !hasNonDefaultTerms;

    if (hasNoTerms) {
      setCanProgress(true);
      return;
    }
    if (hasOnlyNonDefaultTerms) {
      const nonDefaultTermChecked = walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.NONDEFAULT_TERMS];
      setCanProgress(nonDefaultTermChecked);
      return;
    }
    setCanProgress(walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.TERMS]);
  }, [hasNormalTerms, hasNonDefaultTerms, setCanProgress, walletForm]);

  const handleTermsChecked = useCallback(
    (checked) => {
      updateWalletForm([FORM_REQUIREMENTS.METADATA, FORM_REQUIREMENTS.TERMS], checked);
    },
    [updateWalletForm],
  );

  const handleNonDefaultTermsChecked = () => {
    return (checked) => {
      updateWalletForm([FORM_REQUIREMENTS.METADATA, FORM_REQUIREMENTS.NONDEFAULT_TERMS], checked);
    };
  };

  return (
    <FlexColumn className={styles['left-side']}>
      <Text textType={TextType.H2} value={messages.updateModalLeftHeader} />
      <Text textType={TextType.Paragraph} value={messages.updateModalLeftInfo} />
      <Text textType={TextType.Paragraph} value={messages.updateModalLeftSubHeader} />

      {hasNormalTerms && (
        <>
          <AcceptTerms
            checkboxLabel={messages.updateModalLeftSideAgreement}
            handleTermsChecked={handleTermsChecked}
            isChecked={walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.TERMS]}
          />
          <div className={styles['terms-container']}>
            {terms.map((term) => {
              const termMessage = termsMessages[term.termType] || termsMessages.default;
              const version = term.version === DEFAULT_WALLET_TERMS_VERSION ? Versions.default : term.version;
              return (
                <TermLink
                  key={term.termType}
                  iconClass={styles['external-link-icon']}
                  termKey={term.termType}
                  termText={termMessage}
                  version={version}
                />
              );
            })}
          </div>
        </>
      )}
      {hasNonDefaultTerms && (
        <>
          <AcceptTerms
            checkboxLabel={termsMessages.NonDefaultTerms}
            handleTermsChecked={handleNonDefaultTermsChecked()}
            isChecked={walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.NONDEFAULT_TERMS]}
          />
          <div className={styles['terms-container']}>
            {Object.entries(nonDefaultTerms).map(([labelProviderId, nonDefaultTermList]) => {
              return nonDefaultTermList.map((term) => {
                const termText = term.termLink?.title ?? termsMessages.default;
                const version = term.version === DEFAULT_WALLET_TERMS_VERSION ? Versions.default : term.version;
                return (
                  <TermLink
                    key={labelProviderId}
                    iconClass={styles['external-link-icon']}
                    termKey={term.termType}
                    termText={termText}
                    version={version}
                  />
                );
              });
            })}
          </div>
        </>
      )}
      {hasInsurance && shouldShowInsuranceToggle && (
        <div className={styles['auto-apply-insurance']}>
          <div className={styles['terms-title']}>
            <FormattedMessage {...messages.autoInsureTitle} />
            <span className={styles.optional}>
              <FormattedMessage {...messages.optional} />
            </span>
          </div>
          <div>
            <Checkbox
              checked={shouldAutoInsure}
              className={styles['terms-checkbox']}
              onChange={() => {
                setShouldAutoInsure((prev) => {
                  const next = !prev;
                  trackWalletToggle({ insuranceProvider: insuranceProvider?.name, toggleValue: next });
                  return next;
                });
              }}
            >
              <span className={styles['terms-checkbox-label']}>
                <FormattedMessage {...messages.autoInsureLabel} values={{ insuranceProviderName }} />
              </span>
            </Checkbox>
          </div>
          <div className={styles.disclaimer}>
            <FormattedMessage {...messages.autoInsureDisclaimer} />
          </div>
          <div className={styles.disclaimer}>
            <FormattedMessage
              {...messages.autoInsureDisclaimer2}
              values={{
                a: (chunks) => (
                  <InsuranceTermsLink
                    insuranceProviderId={newInsuranceProviderIds[0]}
                    source={EventSourceStrings.ADD_FUNDS_WALLET_REGISTRATION_STEP}
                  >
                    {chunks}
                  </InsuranceTermsLink>
                ),
              }}
            />
          </div>
        </div>
      )}
    </FlexColumn>
  );
};

TermsAndConditions.propTypes = {
  setCanProgress: PropTypes.func.isRequired,
  newInsuranceProviderIds: PropTypes.arrayOf(PropTypes.string),
};
